import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  MapPin,
  Users,
  Stethoscope,
  CalendarRange,
  LayoutGrid,
  ListChecks,
  Video,
  ShieldCheck,
  UserPlus,
  Receipt,
  ClipboardPenLine,
  ClipboardCheck,
  UserCog,
  TestTube2,
  FolderKanban,
  Boxes,
  Image,
  CreditCard,
} from 'lucide-react';
import { useI18n } from '../i18n/LanguageContext';
import { useAuth0 } from '@auth0/auth0-react';
import { API_BASE, AUTH_AUDIENCE } from '../config/env';
import { filterMvpSettingsCards } from '../config/mvpVisibility';
import { SETTINGS_PERMISSION_GROUPS, permissionCodeMap } from '../lib/permissionCatalog';

type CurrentUser = {
  role?: string;
  roles?: string[];
  permissionCodes?: string[];
  featureFlags?: {
    treatmentCatalogEnabled?: boolean;
  };
};

const settingsItOverrides: Record<string, string> = {
  'Loading settings...': 'Caricamento impostazioni...',
  'Room Management': 'Gestione stanze',
  'Manual Tests': 'Test manuali',
  'Document Types': 'Tipi di documento',
  'External Users': 'Utenti esterni',
  'Manage your organization details, branding, and contact info.': 'Gestisci i dettagli dell’organizzazione, il branding e i contatti.',
  'Configure your clinics, facilities, and operating hours.': 'Configura cliniche, strutture e orari operativi.',
  'Manage system users, logins, and access.': 'Gestisci utenti di sistema, accessi e permessi.',
  'Define user roles and access control policies.': 'Definisci ruoli utente e criteri di controllo accessi.',
  'Manage provider profiles, licenses, and signatures.': 'Gestisci profili dei provider, licenze e firme.',
  'Define visit templates, durations, colors, and billing tags.': 'Definisci modelli visita, durate, colori e tag di fatturazione.',
  'Manage non-clinical users for front desk, billing, and office operations.':
    'Gestisci utenti non clinici per front desk, fatturazione e operazioni d’ufficio.',
  'Manage rooms, equipment, and other schedulable assets.': 'Gestisci stanze, attrezzature e altre risorse schedulabili.',
  'Upload floor plans, draw shared-room zones, and control the visual room board.':
    'Carica planimetrie, disegna zone condivise e controlla la board visiva delle stanze.',
  'Control automation, conflicts, reminders, and calendar views.': 'Controlla automazioni, conflitti, promemoria e viste calendario.',
  'Setup video providers, waiting rooms, and consent forms.': 'Configura provider video, sale d’attesa e moduli di consenso.',
  'Configure dynamic image/manual test definitions used inside patient charts.':
    'Configura definizioni dinamiche di test immagine/manuali usate nelle cartelle paziente.',
  'Define document categories and the metadata required during uploads.':
    'Definisci categorie documentali e metadati richiesti durante i caricamenti.',
  'Configure procedures, services, supplies, and pricing used for billing.':
    'Configura procedure, servizi, forniture e prezzi usati per la fatturazione.',
  'Configure products, suppliers, units, lot rules, and reorder thresholds.':
    'Configura prodotti, fornitori, unita, regole lotto e soglie di riordino.',
  'Design reusable clinical/intake forms and configure patient completion workflows.':
    'Progetta moduli clinici/intake riutilizzabili e configura i flussi di completamento paziente.',
  'Manage non-system external contacts who can receive care-plan tasks and complete secure forms.':
    'Gestisci contatti esterni non di sistema che possono ricevere attività del piano di cura e completare moduli sicuri.',
  'Design reusable treatment plans with goals, interventions, monitoring, and escalation logic.':
    'Progetta piani terapeutici riutilizzabili con obiettivi, interventi, monitoraggio e logiche di escalation.',
  'Manage your personal profile, language, timezone, notifications, and signature.':
    'Gestisci profilo personale, lingua, fuso orario, notifiche e firma.',
};

function createSettingsTranslator(language: string, tr: (value: string) => string) {
  if (language !== 'it') return tr;
  return (phrase: string) => {
    const normalized = phrase.trim();
    if (!normalized) return phrase;
    const globalTranslation = tr(normalized);
    if (globalTranslation !== normalized) return phrase.replace(normalized, globalTranslation);
    const local = settingsItOverrides[normalized];
    return local ? phrase.replace(normalized, local) : phrase;
  };
}

export default function Settings() {
  const navigate = useNavigate();
  const { language, t, tr } = useI18n();
  const ts = createSettingsTranslator(language, tr);
  const { getAccessTokenSilently } = useAuth0();
  const [roleLoading, setRoleLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [permissionCodes, setPermissionCodes] = useState<string[]>([]);
  const [treatmentCatalogEnabled, setTreatmentCatalogEnabled] = useState(true);

  useEffect(() => {
    let active = true;
    void (async () => {
      setRoleLoading(true);
      try {
        const token = await getAccessTokenSilently({ authorizationParams: { audience: AUTH_AUDIENCE } });
        const res = await fetch(`${API_BASE}/users/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Unable to resolve user role.');
        const me = (await res.json()) as CurrentUser;
        const roles = (Array.isArray(me.roles) ? me.roles : [me.role]).map((entry) => String(entry || '').toUpperCase());
        if (active) setIsAdmin(roles.includes('ADMIN'));
        if (active) {
          setPermissionCodes(Array.isArray(me.permissionCodes) ? me.permissionCodes.map((entry) => String(entry || '').trim()).filter(Boolean) : []);
          setTreatmentCatalogEnabled(me.featureFlags?.treatmentCatalogEnabled ?? true);
        }
      } catch {
        if (active) setIsAdmin(false);
        if (active) setPermissionCodes([]);
        if (active) setTreatmentCatalogEnabled(true);
      } finally {
        if (active) setRoleLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [getAccessTokenSilently]);

  const cards = [
    {
      title: 'Business Details',
      description: 'Manage core business details, branding, templates, and account settings.',
      icon: Building2,
      path: '/settings/profile',
      color: 'bg-blue-50 text-blue-600',
      adminOnly: true,
      permissionTitle: 'Organization Profile',
    },
    {
      title: 'Locations',
      description: 'Configure your clinics, facilities, and operating hours.',
      icon: MapPin,
      path: '/settings/locations',
      color: 'bg-emerald-50 text-emerald-600',
      adminOnly: false,
      permissionTitle: 'Locations',
    },
    {
      title: 'Team',
      description: 'Manage staff profiles, logins, locations, and access.',
      icon: Users,
      path: '/settings/users',
      color: 'bg-violet-50 text-violet-600',
      adminOnly: true,
      permissionTitle: 'Team',
    },
    {
      title: 'Roles & Permissions',
      description: 'Define user roles and access control policies.',
      icon: ShieldCheck,
      path: '/settings/roles',
      color: 'bg-amber-50 text-amber-600',
      adminOnly: true,
      permissionTitle: 'Roles & Permissions',
    },
    {
      title: 'Providers',
      description: 'Manage provider profiles, licenses, and signatures.',
      icon: Stethoscope,
      path: '/settings/providers',
      color: 'bg-rose-50 text-rose-600',
      adminOnly: false,
      permissionTitle: 'Providers',
    },
    {
      title: 'Appointment Types',
      description: 'Define visit templates, durations, colors, and billing tags.',
      icon: ListChecks,
      path: '/settings/appointmenttypes',
      color: 'bg-indigo-50 text-indigo-600',
      adminOnly: false,
      permissionTitle: 'Appointment Types',
    },
    {
      title: 'Practice Users',
      description: 'Manage non-clinical users for front desk, billing, and office operations.',
      icon: UserPlus,
      path: '/settings/practice-users',
      color: 'bg-cyan-50 text-cyan-600',
      adminOnly: false,
      permissionTitle: 'Practice Users',
    },
    {
      title: 'Resources Hub',
      description: 'Open staff, asset, room, and schedule resource setup from one place.',
      icon: LayoutGrid,
      path: '/settings/resources',
      color: 'bg-slate-100 text-slate-700',
      adminOnly: false,
      permissionTitle: 'Resources Hub',
    },
    {
      title: 'Schedule Assets',
      description: 'Manage rooms, equipment, and other schedulable assets.',
      icon: CalendarRange,
      path: '/settings/schedule-assets',
      color: 'bg-indigo-50 text-indigo-600',
      adminOnly: false,
      permissionTitle: 'Schedule Assets',
    },
    {
      title: 'Room Management',
      description: 'Upload floor plans, draw shared-room zones, and control the visual room board.',
      icon: LayoutGrid,
      path: '/settings/room-management',
      color: 'bg-emerald-50 text-emerald-700',
      adminOnly: false,
      permissionTitle: 'Room Management',
    },
    {
      title: 'Scheduling',
      description: 'Control automation, conflicts, reminders, and calendar views.',
      icon: CalendarRange,
      path: '/settings/scheduling',
      color: 'bg-indigo-50 text-indigo-600',
      adminOnly: false,
      permissionTitle: 'Scheduling',
    },
    {
      title: 'Telemedicine',
      description: 'Setup video providers, waiting rooms, and consent forms.',
      icon: Video,
      path: '/settings/telemed',
      color: 'bg-fuchsia-50 text-fuchsia-600',
      adminOnly: false,
      permissionTitle: 'Telemedicine',
    },
    {
      title: 'Manual Tests',
      description: 'Configure dynamic image/manual test definitions used inside patient charts.',
      icon: TestTube2,
      path: '/settings/manual-tests',
      color: 'bg-amber-50 text-amber-700',
      adminOnly: false,
      permissionTitle: 'Manual Tests',
    },
    {
      title: 'Document Types',
      description: 'Define document categories and the metadata required during uploads.',
      icon: FolderKanban,
      path: '/settings/document-types',
      color: 'bg-orange-50 text-orange-700',
      adminOnly: false,
      permissionTitle: 'Document Types',
    },
    {
      title: 'Billables Catalog',
      description: 'Configure procedures, services, supplies, and pricing used for billing.',
      icon: Receipt,
      path: '/settings/billing-catalog',
      color: 'bg-teal-50 text-teal-700',
      adminOnly: false,
      permissionTitle: 'Billables Catalog',
    },
    {
      title: 'Payments',
      description: 'Connect Stripe to take card payments, deposits, and online invoice payments.',
      icon: CreditCard,
      path: '/settings/payments',
      color: 'bg-indigo-50 text-indigo-700',
      adminOnly: true,
      permissionTitle: 'Billables Catalog',
    },
    {
      title: 'Inventory Setup',
      description: 'Configure products, suppliers, units, lot rules, and reorder thresholds.',
      icon: Boxes,
      path: '/settings/inventory',
      color: 'bg-emerald-50 text-emerald-700',
      adminOnly: false,
      permissionTitle: 'Inventory Setup',
    },
    {
      title: 'Medical Forms',
      description: 'Design reusable clinical/intake forms and configure patient completion workflows.',
      icon: ClipboardPenLine,
      path: '/settings/medical-forms',
      color: 'bg-sky-50 text-sky-700',
      adminOnly: false,
      permissionTitle: 'Medical Forms',
    },
    {
      title: 'Charts',
      description: 'Upload treatment-area charts and transparent stencils for clinical documentation.',
      icon: Image,
      path: '/settings/charts',
      color: 'bg-cyan-50 text-cyan-700',
      adminOnly: false,
      permissionTitle: 'Charts',
    },
    {
      title: 'External Users',
      description: 'Manage non-system external contacts who can receive care-plan tasks and complete secure forms.',
      icon: UserPlus,
      path: '/settings/external-users',
      color: 'bg-lime-50 text-lime-700',
      adminOnly: false,
      permissionTitle: 'External Users',
    },
    {
      title: 'Treatment Plans',
      description: 'Design reusable treatment plans with goals, interventions, monitoring, and escalation logic.',
      icon: ClipboardCheck,
      path: '/settings/treatment-plans',
      color: 'bg-emerald-50 text-emerald-700',
      adminOnly: false,
      permissionTitle: 'Treatment Plans',
    },
    {
      title: 'My Account',
      description: 'Manage your personal profile, language, timezone, notifications, and signature.',
      icon: UserCog,
      path: '/settings/account',
      color: 'bg-slate-100 text-slate-700',
      adminOnly: false,
      permissionTitle: 'My Account',
    },
  ];

  const settingsPermissionCodes = permissionCodeMap(SETTINGS_PERMISSION_GROUPS);
  const permissionSet = new Set(permissionCodes);
  const hasPermissionCatalog = permissionSet.size > 0;
  const visibleCards = filterMvpSettingsCards(cards)
    .filter((card) => card.path !== '/settings/appointmenttypes' || treatmentCatalogEnabled)
    .filter((card) => {
    if (isAdmin) return true;
    if (hasPermissionCatalog) {
      const required = settingsPermissionCodes[card.permissionTitle] || [];
      return required.some((code) => permissionSet.has(code));
    }
    return !card.adminOnly || isAdmin;
  });

  return (
    <div className="space-y-6">
      <div>
        <p className="text-sm uppercase tracking-wide text-slate-400">{t('settings.section', 'Settings')}</p>
        <h1 className="text-2xl font-semibold text-ink">{t('settings.title', 'Configuration')}</h1>
      </div>

      {roleLoading ? (
        <div className="text-sm text-slate-500">{ts('Loading settings...')}</div>
      ) : null}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 2xl:grid-cols-5 gap-3">
        {visibleCards.map((card) => (
          <button
            key={card.path}
            onClick={() => navigate(card.path)}
            className="flex min-h-[180px] flex-col items-start rounded-xl border border-slate-200 bg-white p-4 text-left shadow-sm transition-all hover:border-slate-300 hover:shadow-md group"
          >
            <div className={`mb-3 flex h-10 w-10 items-center justify-center rounded-lg ${card.color}`}>
              <card.icon size={20} />
            </div>
            <h3 className="mb-1 text-base font-semibold leading-snug group-hover:text-ink">
              {card.path === '/settings/profile'
                ? t('settings.profile', card.title)
                : card.path === '/settings/locations'
                  ? t('settings.locations', card.title)
                : card.path === '/settings/users'
                    ? t('settings.users', card.title)
                    : card.path === '/settings/roles'
                      ? t('settings.roles', card.title)
                  : card.path === '/settings/providers'
                          ? t('settings.providers', card.title)
                          : card.path === '/settings/appointmenttypes'
                          ? t('settings.appointmentTypes', card.title)
                          : card.path === '/settings/practice-users'
                            ? t('settings.practiceUsers', card.title)
                            : card.path === '/settings/resources'
                              ? ts(card.title)
                            : card.path === '/settings/schedule-assets'
                              ? t('settings.scheduleAssets', card.title)
                            : card.path === '/settings/room-management'
                              ? ts(card.title)
                              : card.path === '/settings/scheduling'
                                ? t('settings.scheduling', card.title)
                              : card.path === '/settings/telemed'
                                ? t('settings.telemed', card.title)
                                : card.path === '/settings/manual-tests'
                                    ? ts(card.title)
                                    : card.path === '/settings/billing-catalog'
                                      ? t('settings.billingCatalog', card.title)
                                      : card.path === '/settings/inventory'
                                        ? ts(card.title)
                                    : card.path === '/settings/medical-forms'
                                        ? t('settings.medicalForms', card.title)
                                        : card.path === '/settings/document-types'
                                          ? ts(card.title)
                                        : card.path === '/settings/external-users'
                                          ? ts(card.title)
                                        : card.path === '/settings/treatment-plans'
                                          ? t('settings.treatmentPlans', card.title)
                                          : card.path === '/settings/account'
                                            ? t('settings.myAccount', card.title)
                                            : ts(card.title)}
            </h3>
            <p className="text-sm leading-6 text-slate-500">
              {ts(card.description)}
            </p>
          </button>
        ))}
      </div>
    </div>
  );
}
